/**
 * Icon tinting — recolors monochrome SVG icons to match the theme's --primary.
 * Uses mask-image on the <img> itself with a transparent src swap.
 *
 * Shared by:
 *   - cleanSectionHTML (export path, via DOMParser)
 *   - iframe bridge script (inlined copy, since bridge can't import modules)
 */

const TRANSPARENT_SVG = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22/%3E';

/**
 * Detect if a URL points to an SVG file. Handles query strings and CDN params.
 */
export function isSvgUrl(src) {
  try {
    return new URL(src, 'https://x').pathname.toLowerCase().endsWith('.svg');
  } catch {
    return false;
  }
}

function canUseSvgMask(src, root, icon) {
  const raw = String(src || '').trim();
  if (!raw) return false;

  if (raw.startsWith('data:') || raw.startsWith('blob:')) return true;

  const doc = icon?.ownerDocument || root?.ownerDocument || (root?.nodeType === 9 ? root : null);
  const base = doc?.baseURI || globalThis.location?.href || '';
  const docOrigin = doc?.location?.origin || (base ? new URL(base).origin : '');
  if (!docOrigin || docOrigin === 'null') return false;

  try {
    const resolved = new URL(raw, base || 'https://x');
    if (resolved.protocol !== 'http:' && resolved.protocol !== 'https:') return true;
    return resolved.origin === docOrigin;
  } catch {
    return false;
  }
}

/**
 * Apply icon tinting to all img.cs-icon elements in a DOM root.
 * Works with both browser DOM and DOMParser-produced documents.
 * Idempotent — skips already-tinted icons.
 */
export function applyIconTintToDOM(root) {
  const icons = root.querySelectorAll('img.cs-icon:not([data-tinted])');
  for (const icon of icons) {
    const src = icon.getAttribute('src');
    if (!src || !isSvgUrl(src) || !canUseSvgMask(src, root, icon)) continue;

    icon.setAttribute('data-orig-src', src);
    icon.setAttribute('data-tinted', '');

    // Apply mask styles inline — each icon has a unique src
    const tintColor = icon.getAttribute('data-tint-color');
    const tintVar = tintColor
      ? tintColor.startsWith('#')
        ? tintColor
        : `var(${tintColor})`
      : null;
    const maskStyles = [
      `-webkit-mask-image:url("${src}")`,
      `mask-image:url("${src}")`,
      '-webkit-mask-size:contain',
      'mask-size:contain',
      '-webkit-mask-repeat:no-repeat',
      'mask-repeat:no-repeat',
      '-webkit-mask-position:center',
      'mask-position:center',
      'background-color:var(--pb-icon-tint, var(--primary))',
      ...(tintVar ? [`--pb-icon-tint:${tintVar}`] : [])
    ].join(';');

    // Preserve existing inline styles
    const existing = icon.getAttribute('style') || '';
    icon.setAttribute(
      'style',
      existing ? existing.replace(/;?\s*$/, ';') + maskStyles : maskStyles
    );

    // Swap to transparent src so the mask + background is all that renders
    icon.setAttribute('src', TRANSPARENT_SVG);
  }
}

/**
 * Remove icon tinting from all tinted icons in a DOM root.
 * Restores original src and removes mask styles.
 */
export function removeIconTintFromDOM(root) {
  const icons = root.querySelectorAll('img.cs-icon[data-tinted]');
  for (const icon of icons) {
    const orig = icon.getAttribute('data-orig-src');
    if (orig) icon.setAttribute('src', orig);

    icon.removeAttribute('data-tinted');
    icon.removeAttribute('data-orig-src');

    // Remove mask-related inline styles (preserve others)
    const style = icon.getAttribute('style') || '';
    const cleaned = style
      .split(';')
      .filter((s) => {
        const prop = s.split(':')[0]?.trim().toLowerCase();
        return (
          prop &&
          !prop.includes('mask-image') &&
          !prop.includes('mask-size') &&
          !prop.includes('mask-repeat') &&
          !prop.includes('mask-position') &&
          prop !== 'background-color' &&
          prop !== '--pb-icon-tint'
        );
      })
      .join(';')
      .replace(/^;+|;+$/g, '');

    if (cleaned) icon.setAttribute('style', cleaned);
    else icon.removeAttribute('style');
  }
}
